import { lazy, Suspense, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Link2,
  MousePointerClick,
  ArrowUpRight,
  Sparkles,
  TrendingUp,
  Eye,
  ScanLine,
  MessageSquare,
  Zap,
  Send,
  Package,
  Phone,
  Play,
  Tag,
  Lightbulb,
} from 'lucide-react';
import { useAuth } from '@/client/hooks/use-auth';
import { useFeatures, TIER_NAMES } from '@/client/hooks/use-features';
import { formatNumber, cn } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { getDashboardServices, type ServiceId } from '@/client/lib/config';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Skeleton } from '@/client/components/ui/skeleton';
import { Progress } from '@/client/components/ui/progress';
import { QuickCreate } from '@/client/components/quick-create';

// Lazy load the Insights Wizard for code splitting
const InsightsWizardModal = lazy(() =>
  import('@/client/components/insights/insights-wizard-modal').then((m) => ({
    default: m.InsightsWizardModal,
  }))
);

interface DashboardLink {
  shortCode: string;
  destinationUrl: string;
  title?: string;
  tags?: string[];
  totalClicks?: number;
}

// Get greeting based on time of day
function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// Asset card component for the overview grid
interface AssetCardProps {
  title: string;
  description: string;
  href: string;
  icon: React.ElementType;
  gradient: string;
  stats: { label: string; value: number | string; icon?: React.ElementType }[];
  isLoading?: boolean;
  comingSoon?: boolean;
}

function AssetCard({
  title,
  description,
  href,
  icon: Icon,
  gradient,
  stats,
  isLoading,
  comingSoon,
}: AssetCardProps) {
  return (
    <Link to={href} className="block group touch-target">
      <Card className="h-full rounded-2xl border-0 overflow-hidden bg-gradient-to-br from-card to-muted/20 hover:shadow-xl hover:shadow-black/5 dark:hover:shadow-black/20 transition-all duration-500 group-hover:-translate-y-1">
        <CardHeader className="pb-2">
          {/* Header row with icon and badge - Flexbox micro-layout */}
          <div className="flex items-start justify-between gap-2">
            <div
              className={`size-12 shrink-0 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}
            >
              <Icon className="size-6 text-white" />
            </div>
            {comingSoon ? (
              <Badge variant="secondary" className="text-[10px] shrink-0">
                Coming Soon
              </Badge>
            ) : (
              <ArrowUpRight className="size-5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
            )}
          </div>
          <CardTitle className="text-lg mt-3 truncate">{title}</CardTitle>
          <p className="text-sm text-muted-foreground line-clamp-2">{description}</p>
        </CardHeader>
        <CardContent className="pt-2">
          {/* Stats row - Flexbox with wrap for smaller containers */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {stats.map((stat, i) => (
              <div key={i} className="flex items-center gap-1.5 min-w-0">
                {stat.icon && <stat.icon className="size-3.5 shrink-0 text-muted-foreground" />}
                {isLoading ? (
                  <Skeleton className="h-5 w-12" />
                ) : (
                  <span className="text-sm font-semibold tabular-nums">
                    {typeof stat.value === 'number' ? formatNumber(stat.value) : stat.value}
                  </span>
                )}
                <span className="text-xs text-muted-foreground truncate">{stat.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// Helper to get stats configuration for each service type
interface AssetCardStats {
  totalLinks: number;
  totalClicks: number;
  qrCodesCount: number;
  pagesCount: number;
  formsCount: number;
  linksLoading: boolean;
}

function getAssetCardConfig(
  serviceId: ServiceId,
  stats: AssetCardStats
): {
  stats: { label: string; value: number | string; icon?: React.ElementType }[];
  isLoading: boolean;
} {
  switch (serviceId) {
    case 'links':
      return {
        stats: [
          { label: 'links', value: stats.totalLinks },
          { label: 'clicks', value: stats.totalClicks, icon: MousePointerClick },
        ],
        isLoading: stats.linksLoading,
      };
    case 'qr_codes':
      return {
        stats: [
          { label: 'codes', value: stats.qrCodesCount },
          { label: 'scans', value: 0, icon: ScanLine },
        ],
        isLoading: false,
      };
    case 'pages':
      return {
        stats: [
          { label: 'pages', value: stats.pagesCount },
          { label: 'views', value: 0, icon: Eye },
        ],
        isLoading: false,
      };
    case 'forms':
      return {
        stats: [
          { label: 'forms', value: stats.formsCount },
          { label: 'responses', value: 0, icon: MessageSquare },
        ],
        isLoading: false,
      };
    case 'email_newsletter':
      return {
        stats: [
          { label: 'subscribers', value: 0 },
          { label: 'sent', value: 0, icon: Send },
        ],
        isLoading: false,
      };
    case 'direct_mail':
      return {
        stats: [
          { label: 'pieces', value: 0 },
          { label: 'delivered', value: 0, icon: Package },
        ],
        isLoading: false,
      };
    case 'sms':
      return {
        stats: [
          { label: 'contacts', value: 0 },
          { label: 'sent', value: 0, icon: Phone },
        ],
        isLoading: false,
      };
    case 'workflows':
      return {
        stats: [
          { label: 'workflows', value: 0 },
          { label: 'runs', value: 0, icon: Play },
        ],
        isLoading: false,
      };
    default:
      return { stats: [], isLoading: false };
  }
}

export function DashboardPage() {
  const { user } = useAuth();
  const { tier } = useFeatures();
  const [isInsightsOpen, setInsightsOpen] = useState(false);

  // Get user's first name with Armenian warmth
  const firstName = user?.name?.split(' ')[0] || 'there';
  const greetingName = user?.name ? `${firstName} jan` : firstName;

  // Fetch links data for stats
  const { data: linksData, isLoading: linksLoading } = useQuery({
    queryKey: ['links', 'dashboard-stats'],
    queryFn: async () => {
      const response = await apiFetch('/api/links?itemsPerPage=5&orderBy=createdAt&orderDir=desc');
      if (!response.ok) throw new Error('Failed to fetch links');
      return response.json();
    },
  });

  // Fetch monthly usage data
  const { data: usageData, isLoading: usageLoading } = useQuery({
    queryKey: ['links', 'usage'],
    queryFn: async () => {
      const response = await apiFetch('/api/links/usage');
      if (!response.ok) throw new Error('Failed to fetch usage');
      return response.json() as Promise<{
        used: number;
        limit: number | null;
        remaining: number | null;
        percentUsed: number;
        resetsAt: string;
      }>;
    },
  });

  // Calculate stats
  const totalLinks = linksData?.pagination?.totalItems ?? 0;
  const totalClicks =
    linksData?.data?.reduce(
      (sum: number, link: DashboardLink) => sum + (link.totalClicks ?? 0),
      0
    ) ?? 0;

  // Placeholder stats for coming soon features
  const qrCodesCount = 0;
  const pagesCount = 0;
  const formsCount = 0;

  return (
    <div className="space-y-6 lg:space-y-8 stagger-children">
      {/* Welcome Header */}
      <header>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl truncate">
          {getGreeting()}, {greetingName}!
        </h1>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Here&apos;s an overview of your workspace
        </p>
      </header>

      {/* Quick Create - The 30-second win */}
      <QuickCreate />

      {/* Overall Stats - CSS Grid with auto-fit for responsive columns */}
      <section className="grid-stats" aria-label="Statistics overview">
        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 shrink-0 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Link2 className="size-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-muted-foreground font-medium">Total Links</p>
              {linksLoading ? (
                <Skeleton className="h-7 w-16" />
              ) : (
                <p className="text-2xl font-bold tabular-nums">{formatNumber(totalLinks)}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 shrink-0 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <MousePointerClick className="size-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-muted-foreground font-medium">Total Clicks</p>
              {linksLoading ? (
                <Skeleton className="h-7 w-16" />
              ) : (
                <p className="text-2xl font-bold tabular-nums">{formatNumber(totalClicks)}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 shrink-0 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <TrendingUp className="size-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-muted-foreground font-medium">Avg. per Link</p>
              {linksLoading ? (
                <Skeleton className="h-7 w-16" />
              ) : (
                <p className="text-2xl font-bold tabular-nums">
                  {totalLinks > 0 ? formatNumber(Math.round(totalClicks / totalLinks)) : '0'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl py-0 border-0 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="size-12 shrink-0 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Sparkles className="size-6 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-muted-foreground font-medium">Total Assets</p>
              {linksLoading ? (
                <Skeleton className="h-7 w-16" />
              ) : (
                <p className="text-2xl font-bold tabular-nums">
                  {formatNumber(totalLinks + qrCodesCount + pagesCount + formsCount)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Monthly Usage Meter */}
      {usageData && usageData.limit !== null && (
        <Card
          className={cn(
            'rounded-2xl border-0 overflow-hidden',
            usageData.percentUsed >= 90
              ? 'bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20'
              : usageData.percentUsed >= 75
                ? 'bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20'
                : 'bg-gradient-to-br from-card to-muted/20'
          )}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div
                  className={cn(
                    'size-10 rounded-xl flex items-center justify-center',
                    usageData.percentUsed >= 90
                      ? 'bg-gradient-to-br from-red-500 to-orange-500'
                      : usageData.percentUsed >= 75
                        ? 'bg-gradient-to-br from-amber-500 to-yellow-500'
                        : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                  )}
                >
                  <Zap className="size-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium">Links this month</span>
                    {usageLoading ? (
                      <Skeleton className="h-4 w-20" />
                    ) : (
                      <span className="text-sm font-semibold tabular-nums">
                        {usageData.used} / {usageData.limit}
                      </span>
                    )}
                  </div>
                  {usageLoading ? (
                    <Skeleton className="h-2 w-full" />
                  ) : (
                    <Progress
                      value={usageData.percentUsed}
                      className={cn(
                        'h-2',
                        usageData.percentUsed >= 90
                          ? '[&>div]:bg-gradient-to-r [&>div]:from-red-500 [&>div]:to-orange-500'
                          : usageData.percentUsed >= 75
                            ? '[&>div]:bg-gradient-to-r [&>div]:from-amber-500 [&>div]:to-yellow-500'
                            : '[&>div]:bg-gradient-to-r [&>div]:from-blue-500 [&>div]:to-cyan-500'
                      )}
                    />
                  )}
                </div>
              </div>

              {tier !== 'pro' && usageData.percentUsed >= 50 && (
                <Button
                  asChild
                  variant={usageData.percentUsed >= 90 ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'rounded-lg whitespace-nowrap',
                    usageData.percentUsed >= 90 &&
                      'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white border-0'
                  )}
                >
                  <Link to="/settings/billing">
                    {usageData.percentUsed >= 90
                      ? 'Upgrade Now'
                      : `Get ${TIER_NAMES[tier === 'free' ? 'plus' : 'pro']}`}
                  </Link>
                </Button>
              )}
            </div>

            {usageData.percentUsed >= 100 && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                You&apos;ve reached your monthly limit. Upgrade to create more links.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Insights Wizard CTA */}
      {totalClicks > 0 && (
        <Card className="rounded-2xl border-0 overflow-hidden bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/20 dark:to-purple-950/20">
          <CardContent className="flex items-center justify-between gap-4 p-4">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shadow-lg shadow-violet-500/20">
                <Lightbulb className="size-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="font-medium">Discover your insights</p>
                <p className="text-sm text-muted-foreground">
                  Get personalized recommendations based on your data
                </p>
              </div>
            </div>
            <Button
              onClick={() => setInsightsOpen(true)}
              variant="outline"
              className="rounded-xl gap-2 border-violet-200 dark:border-violet-800 hover:bg-violet-100 dark:hover:bg-violet-900/30"
            >
              <Lightbulb className="size-4" />
              Get Insights
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Asset Cards Grid - CSS Grid with auto-fill for optimal card distribution */}
      <section aria-label="Your assets">
        <h2 className="text-lg font-semibold mb-4">Your Assets</h2>
        <div className="grid-assets">
          {getDashboardServices().map((service) => {
            // Get stats and config for each service type
            const serviceConfig = getAssetCardConfig(service.id, {
              totalLinks,
              totalClicks,
              qrCodesCount,
              pagesCount,
              formsCount,
              linksLoading,
            });

            return (
              <AssetCard
                key={service.id}
                title={service.shortName}
                description={service.description}
                href={service.ctaLink}
                icon={service.icon}
                gradient={service.gradient}
                stats={serviceConfig.stats}
                isLoading={serviceConfig.isLoading}
                comingSoon={!service.available}
              />
            );
          })}
        </div>
      </section>

      {/* Recent Links */}
      {linksData?.data?.length > 0 && (
        <Card className="rounded-2xl overflow-hidden py-0 border-0 bg-gradient-to-br from-card to-muted/20">
          <CardHeader className="flex-row items-center justify-between p-6 border-b border-border/50">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Link2 className="size-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg">Recent Links</CardTitle>
                <p className="text-sm text-muted-foreground">Your latest shortened URLs</p>
              </div>
            </div>
            <Button variant="outline" asChild className="rounded-xl gap-1.5">
              <Link to="/links">
                View all
                <ArrowUpRight className="size-4" />
              </Link>
            </Button>
          </CardHeader>

          <CardContent className="p-0">
            <div className="divide-y divide-border/50">
              {linksData.data.slice(0, 5).map((link: DashboardLink) => (
                <Link
                  key={link.shortCode}
                  to={`/links/${link.shortCode}`}
                  className="p-4 flex items-center gap-4 hover:bg-muted/30 transition-all group"
                >
                  <div className="size-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <Link2 className="size-5 text-amber-600 dark:text-amber-400" />
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Title (if available) */}
                    {link.title && (
                      <p className="font-semibold text-sm text-foreground truncate mb-0.5">
                        {link.title}
                      </p>
                    )}
                    {/* Short URL */}
                    <span
                      className={cn(
                        'font-medium text-foreground group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors',
                        link.title && 'text-sm'
                      )}
                    >
                      arml.ink/{link.shortCode}
                    </span>
                    {/* Destination URL */}
                    <p className="text-xs text-muted-foreground truncate">{link.destinationUrl}</p>
                    {/* Tags */}
                    {link.tags && link.tags.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap mt-1">
                        <Tag className="size-3 text-muted-foreground" />
                        {link.tags.slice(0, 2).map((tag: string) => (
                          <Badge
                            key={tag}
                            variant="secondary"
                            className="text-[10px] px-1.5 py-0 font-normal"
                          >
                            {tag}
                          </Badge>
                        ))}
                        {link.tags.length > 2 && (
                          <span className="text-[10px] text-muted-foreground">
                            +{link.tags.length - 2} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="text-right flex-shrink-0">
                    <p className="font-semibold tabular-nums">
                      {formatNumber(link.totalClicks ?? 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">clicks</p>
                  </div>

                  <ArrowUpRight className="size-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state when no links - simplified since QuickCreate is above */}
      {!linksLoading && totalLinks === 0 && (
        <Card className="rounded-2xl border-2 border-dashed border-amber-300 dark:border-amber-800 bg-gradient-to-r from-amber-50/50 to-orange-50/50 dark:from-amber-950/20 dark:to-orange-950/20">
          <CardContent className="flex flex-col items-center text-center py-8 px-6">
            <div className="size-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg mb-4">
              <Link2 className="size-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold mb-1">No links yet</h3>
            <p className="text-muted-foreground text-sm max-w-sm">
              Paste any URL in the box above to create your first shortened link and start tracking
              clicks.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Insights Wizard Modal */}
      <Suspense fallback={null}>
        <InsightsWizardModal isOpen={isInsightsOpen} onClose={() => setInsightsOpen(false)} />
      </Suspense>
    </div>
  );
}
